import React, {FC, useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import {NextState} from '../../../types';
import {useFooterContext} from '../../../hooks';
import styles from './TransferOpenBalancesStep.module.scss';
import {Button, Flex, Text} from '@priority-software/priority-style-react';
import texts from '../../../texts';
import {runTransferOpenBalancesProc} from '../../../actions/transferOpenBalances';
import {Loader} from '../../../Components';
import RoundCheckImage from '../../../../../images/EOY-Round-check.svg';
import classNames from 'classnames';
import {useFiscalYear} from '../../../hooks/useFiscalYear';
import {startProcWithActivationSource} from '../../../../../_shared/GWTBridge/invoke/genPage';
import {ActivationSource} from '../../../../../constants/ActivationSource';

export const TransferOpenBalancesStep: FC = observer(() => {
    const footerState = useFooterContext();
    const [fiscalYearLoaded, fiscalYearState] = useFiscalYear();

    const [result, setResult] = useState<string>();
    const [isTransferring, setIsTransferring] = useState(false);

    useEffect(() => {
        footerState.setIsValid(false);
        window.addEventListener('WizardProcEnd', handleOpenBalancedEndEvent as EventListener);
        return () =>
            window.removeEventListener(
                'WizardProcEnd',
                handleOpenBalancedEndEvent as EventListener
            );
    }, []);

    const handleOpenBalancedEndEvent = (event: CustomEvent<any>) => {
        const status = event?.detail?.data?.status;
        onProcFinished(status);
    };

    const onProcFinished = (result: string) => {
        setIsTransferring(false);
        setResult(result);
        footerState.setIsValid(true);
    };

    useEffect(() => {
        if (footerState.onNextState === NextState.Processing) {
            footerState.setOnNextState(NextState.Done);
        }
    }, [footerState.onNextState]);

    const onButtonClick = () => {
        if (fiscalYearState?.year) {
            setResult(undefined);
            setIsTransferring(true);
            if (fiscalYearState?.subCompanyFlag) {
                startProcWithActivationSource('OPENBALNCES', ActivationSource.EOYWizard);
            } else {
                runTransferOpenBalancesProc(fiscalYearState?.year.toString())
                    .then(result => {
                        onProcFinished(result);
                    })
                    .catch(err => {
                        onProcFinished(err);
                    });
            }
        }
    };

    const isSuccess = (result: string) => {
        return result === 'success';
    };

    return (
        <Flex componentClasses={styles.wrapper} direction={'column'} justifyContent={'flex-start'}>
            <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-700'}>
                {texts['stage2_step1_explanation']}
            </Text>
            <Flex
                componentClasses={classNames(styles.buttons, {
                    [styles.withoutPadding]: isSuccess(result)
                })}
            >
                {!isSuccess(result) && (
                    <Button
                        componentClasses={styles.button}
                        disabled={isTransferring || !fiscalYearLoaded}
                        onClick={() => onButtonClick()}
                        wideButton
                        size={'medium'}
                    >
                        {texts['stage2_step1_name']}
                    </Button>
                )}
            </Flex>
            {isTransferring && (
                <Loader componentClassname={styles.loader} isVisible={isTransferring} />
            )}
            {!!result && (
                <Flex
                    componentClasses={styles.completedBlock}
                    direction={'column'}
                    alignItems={'center'}
                    justifyContent={'center'}
                >
                    {isSuccess(result) && <img alt={''} src={RoundCheckImage} />}
                    <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-700'}>
                        {isSuccess(result)
                            ? texts['stage2_step1_transferred'].replace(
                                  '"XXX"',
                                  fiscalYearState?.year.toString()
                              )
                            : result}
                    </Text>
                </Flex>
            )}
        </Flex>
    );
});
